import { fetchAuthenticatedJson } from './authenticatedFetch';

// CSRF token is kept IN MEMORY only (never localStorage/sessionStorage, never logged).
let csrfToken = null;
let csrfHeaderName = 'X-CSRF-TOKEN';

function resetCsrfToken() {
  csrfToken = null;
}

async function ensureCsrfToken() {
  if (csrfToken) {
    return csrfToken;
  }
  const data = await fetchAuthenticatedJson('/api/manual-trading/csrf');
  csrfToken = data?.token || null;
  csrfHeaderName = data?.headerName || csrfHeaderName;
  return csrfToken;
}

// Unsafe requests require the session CSRF token; if it is unavailable we NEVER send the order.
async function unsafeRequest(url, options = {}) {
  const token = await ensureCsrfToken();
  if (!token) {
    const error = new Error('Jeton CSRF indisponible : la requÃªte a Ã©tÃ© bloquÃ©e par sÃ©curitÃ©.');
    error.csrf = true;
    throw error;
  }
  try {
    return await fetchAuthenticatedJson(url, {
      ...options,
      headers: { ...(options.headers || {}), [csrfHeaderName]: token },
    });
  } catch (error) {
    // A rejected CSRF (403) invalidates the cached token; NO automatic retry of an order.
    if (/403|forbidden|csrf/i.test(String(error?.message))) {
      resetCsrfToken();
      const csrfError = new Error('RequÃªte refusÃ©e par la protection CSRF (rechargez la page pour rÃ©essayer).');
      csrfError.csrf = true;
      csrfError.status = 403;
      throw csrfError;
    }
    throw error;
  }
}

export function fetchManualTradingExchanges() {
  return fetchAuthenticatedJson('/api/manual-trading/exchanges');
}

export function fetchManualTradingSymbols(exchange) {
  return fetchAuthenticatedJson(`/api/manual-trading/${encodeURIComponent(exchange)}/symbols`);
}

export function fetchManualTradingConstraints(exchange, symbol) {
  return fetchAuthenticatedJson(
    `/api/manual-trading/${encodeURIComponent(exchange)}/constraints?symbol=${encodeURIComponent(symbol)}`,
  );
}

export function previewManualTrade(body) {
  return unsafeRequest('/api/manual-trading/preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function createManualTrade(body, idempotencyKey) {
  return unsafeRequest('/api/manual-trading/trades', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(body),
  });
}

export function fetchManualTrades() {
  return fetchAuthenticatedJson('/api/manual-trading/trades');
}

export function fetchManualTrade(id) {
  return fetchAuthenticatedJson(`/api/manual-trading/trades/${id}`);
}

export function reconcileManualTrade(id) {
  return unsafeRequest(`/api/manual-trading/trades/${id}/reconcile`, { method: 'POST' });
}

// Exposed for tests / session changes.
export function resetManualTradingCsrfToken() {
  resetCsrfToken();
}
// LIVE manual execution. Unsafe requests require the session CSRF token; no automatic order retry.
export function fetchBlofinLiveAccountStatus(liveAccountIdentityRef) {
  return fetchAuthenticatedJson(
    `/api/blofin/live/account/status?liveAccountIdentityRef=${encodeURIComponent(liveAccountIdentityRef)}`,
  );
}

export function fetchBlofinLiveInstruments(liveAccountIdentityRef) {
  return fetchAuthenticatedJson(
    `/api/blofin/live/instruments?liveAccountIdentityRef=${encodeURIComponent(liveAccountIdentityRef)}`,
  );
}

export function createBlofinLiveIntent(body) {
  return unsafeRequest('/api/blofin/live/orders/intent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function confirmBlofinLiveOrder(intentId, body) {
  return unsafeRequest(`/api/blofin/live/orders/${encodeURIComponent(intentId)}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function reconcileBlofinLiveOrder(intentId, body) {
  return unsafeRequest(`/api/blofin/live/orders/${encodeURIComponent(intentId)}/reconcile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function fetchBlofinLiveOrder(strategyId, liveAccountIdentityRef) {
  return fetchAuthenticatedJson(
    `/api/blofin/live/orders/${encodeURIComponent(strategyId)}`
      + `?liveAccountIdentityRef=${encodeURIComponent(liveAccountIdentityRef)}`,
  );
}